/**
 * SessionLockGuard.jsx — PROD-1197. The PIN lock for every signed-in route, above the layouts.
 *
 * A layout route (renders <Outlet />). App.jsx puts it around every signed-in route:
 *   - default: routes outside MobileLayout (/onboarding, /security, /2fa-upgrade, /dev/stream).
 *     While the session is locked the lock screen renders INSTEAD of the route, so the
 *     health-profile run is never mounted. On mount it also judges the persisted away stamp
 *     exactly as Home does (a reload or a duplicated tab straight onto the route).
 *   - `keepScreen`: the MobileLayout branch. The screen stays mounted under the lock overlay
 *     and MobileLayout's own hooks keep judging away/idle, as before PROD-1197; only the
 *     lock-screen render moved here, so there is one owner of it.
 *
 * The rule is the pure `lockGuardVerdict` (utils/sessionLockGuard.js). Gate:
 * scripts/check-session-lock-guard.mjs, and the browser unit R28 in check-browser-render.mjs.
 */
import { useLayoutEffect } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useUserStore, useProfileStore } from '../stores'
import PatientSessionLockScreen from './PatientSessionLockScreen'
import {
  lockGuardVerdict,
  GUARD_LOCKED,
  GUARD_LOCK,
  GUARD_LOGOUT,
  GUARD_CONTINUE,
} from '../utils/sessionLockGuard'

export default function SessionLockGuard({ keepScreen = false }) {
  const isAuthenticated = useUserStore((s) => s.isAuthenticated)
  const isLocked = useUserStore((s) => s.isLocked)
  const awayAt = useUserStore((s) => s.awayAt)
  const user = useUserStore((s) => s.user)
  const profile = useProfileStore((s) => s.profile)
  const location = useLocation()
  const navigate = useNavigate()

  const verdict = lockGuardVerdict({
    isAuthenticated, isLocked, awayAt, now: Date.now(), user, profile, judgeAway: !keepScreen,
  })

  // Apply a verdict on the persisted away stamp before the browser paints anything.
  useLayoutEffect(() => {
    const state = useUserStore.getState()
    if (verdict === GUARD_LOCK) {
      // The route with its query, so unlocking returns to the same run (?run=profile).
      state.lockSession(location.pathname + location.search, state.awayAt)
      state.clearAway()
    } else if (verdict === GUARD_LOGOUT) {
      state.clearAway()
      state.logout()
      navigate('/signin', { replace: true })
    } else if (verdict === GUARD_CONTINUE) {
      state.clearAway()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verdict])

  // PROD-1203: the keepScreen branch has ONE element shape in every state, so the screen is
  // never remounted on lock/unlock: <div wrapper> > [<Outlet/>, lock-or-null]. (display: contents
  // keeps the wrapper out of the layout.)
  if (keepScreen) {
    return (
      <div style={{ display: 'contents' }} data-session-lock-guard="keep">
        <Outlet />
        {verdict === GUARD_LOCKED ? <PatientSessionLockScreen /> : null}
      </div>
    )
  }
  if (verdict === GUARD_LOCKED) return <PatientSessionLockScreen />
  // A lock or a sign-out is being applied: render nothing of the route meanwhile.
  if (verdict === GUARD_LOCK || verdict === GUARD_LOGOUT) return null
  return <Outlet />
}
